import { useEffect, useMemo, useRef, useState } from 'react';
import { io, Socket } from 'socket.io-client';
import { Check, Pin, PinOff } from 'lucide-react';
import api from '../lib/api';
import Button from './ui/Button';
import Input from './ui/Input';
import { useAuthStore } from '../stores/authStore';

type Msg = {
  id: string;
  jobId: string;
  senderUserId: string | null;
  body: string;
  createdAt: string;
  readByUserIds: string[];
  pinned?: boolean;
  sender?: { id: string; fullName: string | null; email: string } | null;
};

function renderBodyWithMentions(text: string) {
  const parts = text.split(/(@\S+)/g);
  return parts.map((part, i) => {
    if (part.startsWith('@')) {
      return (
        <span key={i} className="text-accent2 font-semibold">
          {part}
        </span>
      );
    }
    return <span key={i}>{part}</span>;
  });
}

export default function CampaignChat({ jobId }: { jobId: string }) {
  const user = useAuthStore((s) => s.user);
  const [messages, setMessages] = useState<Msg[]>([]);
  const [body, setBody] = useState('');
  const [loading, setLoading] = useState(true);
  const [sending, setSending] = useState(false);
  const [pinning, setPinning] = useState(false);
  const [teamMembers, setTeamMembers] = useState<{ id: string; fullName: string; email: string }[]>([]);
  const [showMentions, setShowMentions] = useState(false);
  const [mentionFilter, setMentionFilter] = useState('');
  const bottomRef = useRef<HTMLDivElement>(null);
  const socketRef = useRef<Socket | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const mentionAnchorRef = useRef<number | null>(null);

  const load = async () => {
    try {
      const res = await api.get<Msg[]>(`/jobs/${jobId}/campaign-messages`);
      setMessages(res.data);
    } catch (e) {
      console.error(e);
    } finally {
      setLoading(false);
    }
  };

  const markOthersRead = async (list: Msg[]) => {
    const uid = useAuthStore.getState().user?.id;
    if (!uid) return;
    const targets = list.filter(
      (m) => m.senderUserId && m.senderUserId !== uid && !m.readByUserIds.includes(uid)
    );
    for (const m of targets) {
      try {
        const { data } = await api.post<Msg>(`/jobs/${jobId}/campaign-messages/${m.id}/read`);
        setMessages((prev) =>
          prev.map((x) => (x.id === data.id ? { ...x, readByUserIds: data.readByUserIds } : x))
        );
      } catch {
        /* ignore */
      }
    }
  };

  useEffect(() => {
    load();
  }, [jobId]);

  useEffect(() => {
    if (!user?.id || loading || messages.length === 0) return;
    markOthersRead(messages);
  }, [user?.id, loading, jobId, messages]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data: job } = await api.get<{ managedByOrgId: string | null }>(`/jobs/${jobId}`);
        const orgId = job?.managedByOrgId;
        if (!orgId || cancelled) return;
        const { data } = await api.get<{ id: string; name: string; email: string }[]>(
          `/organizations/${orgId}/members`
        );
        if (cancelled) return;
        setTeamMembers(
          data.map((m) => ({
            id: m.id,
            fullName: m.name,
            email: m.email,
          }))
        );
      } catch (e) {
        console.warn('Campaign chat team members:', e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [jobId]);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const { data } = await api.post('/auth/socket-token');
        const token = data.token as string;
        const base = import.meta.env.VITE_API_URL?.replace(/\/+$/, '') || '';
        const s = io(base || undefined, {
          path: '/socket.io/',
          auth: { token },
          transports: ['websocket', 'polling'],
        });
        socketRef.current = s;
        s.on('connect', () => {
          s.emit('join-job', jobId, () => {});
        });
        s.on('campaign:message', (msg: Msg) => {
          if (!mounted || msg.jobId !== jobId) return;
          setMessages((prev) =>
            [...prev.filter((m) => m.id !== msg.id), msg].sort(
              (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
            )
          );
        });
        s.on('campaign:message:read', (payload: { messageId: string; userId: string }) => {
          if (!mounted) return;
          setMessages((prev) =>
            prev.map((m) => {
              if (m.id !== payload.messageId) return m;
              const ids = new Set(m.readByUserIds || []);
              ids.add(payload.userId);
              return { ...m, readByUserIds: Array.from(ids) };
            })
          );
        });
      } catch (e) {
        console.warn('Campaign chat socket:', e);
      }
    })();
    return () => {
      mounted = false;
      socketRef.current?.emit('leave-job', jobId);
      socketRef.current?.disconnect();
      socketRef.current = null;
    };
  }, [jobId]);

  const mentionMatches = useMemo(() => {
    const q = mentionFilter.toLowerCase();
    if (!q) return teamMembers.slice(0, 8);
    return teamMembers
      .filter(
        (m) =>
          m.fullName.toLowerCase().includes(q) || m.email.toLowerCase().includes(q)
      )
      .slice(0, 8);
  }, [teamMembers, mentionFilter]);

  const syncMentionFromInput = (value: string, cursor: number | null) => {
    const c = cursor ?? value.length;
    const before = value.slice(0, c);
    const at = before.lastIndexOf('@');
    if (at === -1) {
      mentionAnchorRef.current = null;
      setShowMentions(false);
      return;
    }
    const chunk = before.slice(at + 1);
    if (/\s/.test(chunk)) {
      mentionAnchorRef.current = null;
      setShowMentions(false);
      return;
    }
    mentionAnchorRef.current = at;
    setMentionFilter(chunk);
    setShowMentions(true);
  };

  const pickMention = (member: { fullName: string }) => {
    const at = mentionAnchorRef.current;
    const el = inputRef.current;
    if (at == null || !el) return;
    const cursor = el.selectionStart ?? body.length;
    const tag = `@${member.fullName.trim()}`;
    const next = body.slice(0, at) + tag + body.slice(cursor);
    setBody(next);
    setShowMentions(false);
    mentionAnchorRef.current = null;
    requestAnimationFrame(() => {
      const pos = at + tag.length;
      el.focus();
      el.setSelectionRange(pos, pos);
    });
  };

  const togglePin = async (messageId: string) => {
    try {
      setPinning(true);
      const { data } = await api.patch<Msg>(`/jobs/${jobId}/campaign-messages/${messageId}/pin`);
      setMessages((prev) =>
        prev.map((m) => (m.id === data.id ? { ...m, ...data, sender: data.sender ?? m.sender } : m))
      );
    } catch (e) {
      console.error(e);
    } finally {
      setPinning(false);
    }
  };

  const send = async () => {
    if (!body.trim()) return;
    try {
      setSending(true);
      await api.post(`/jobs/${jobId}/campaign-messages`, { body: body.trim() });
      setBody('');
      await load();
    } catch (e) {
      console.error(e);
      alert('Failed to send');
    } finally {
      setSending(false);
    }
  };

  const pinnedMessages = useMemo(
    () =>
      [...messages.filter((m) => m.pinned)].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    [messages]
  );
  const unpinnedMessages = useMemo(
    () => messages.filter((m) => !m.pinned),
    [messages]
  );

  const renderMessageRow = (m: Msg) => {
    const senderLabel =
      m.sender?.fullName?.trim() ||
      m.sender?.email ||
      (m.senderUserId ? m.senderUserId.slice(0, 8) + '…' : 'Unknown');
    const iRead = user?.id && m.readByUserIds.includes(user.id);

    return (
      <div
        key={m.id}
        className="text-sm border-b border-glass pb-2 group flex gap-2 items-start"
      >
        <div className="flex-1 min-w-0">
          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-glass-text-3">
            <span className="font-medium text-glass-text-2">{senderLabel}</span>
            <span>{new Date(m.createdAt).toLocaleString()}</span>
            {m.pinned && (
              <span className="rounded bg-amber-400/15 text-amber-500 dark:text-amber-300 border border-amber-400/30 px-1.5 py-0.5 text-[10px] font-semibold uppercase tracking-wide">
                Pinned
              </span>
            )}
            {iRead && (
              <Check className="w-3.5 h-3.5 text-emerald-500 dark:text-emerald-300 shrink-0" aria-label="Read" />
            )}
          </div>
          <div className="text-glass-text whitespace-pre-wrap mt-1">
            {renderBodyWithMentions(m.body)}
          </div>
        </div>
        <button
          type="button"
          title={m.pinned ? 'Unpin' : 'Pin'}
          disabled={pinning}
          onClick={() => togglePin(m.id)}
          className="shrink-0 p-1 rounded text-glass-text-3 hover:text-glass-text opacity-60 group-hover:opacity-100 disabled:opacity-30"
        >
          {m.pinned ? <PinOff className="w-4 h-4" /> : <Pin className="w-4 h-4" />}
        </button>
      </div>
    );
  };

  if (loading) {
    return <div className="text-sm text-glass-text-3 py-8 text-center">Loading chat…</div>;
  }

  return (
    <div className="ph-no-capture flex flex-col h-[480px] glass-card">
      <div className="flex-1 overflow-y-auto glass-scroll p-4 space-y-3">
        {messages.length === 0 && (
          <p className="text-sm text-glass-text-3 text-center py-8">No messages yet. Start the conversation.</p>
        )}
        {pinnedMessages.length > 0 && (
          <div className="mb-4 pb-3 border-b border-amber-400/30">
            <div className="text-xs font-semibold text-amber-500 dark:text-amber-300 mb-2">Pinned</div>
            <div className="space-y-3">{pinnedMessages.map((m) => renderMessageRow(m))}</div>
          </div>
        )}
        {unpinnedMessages.map((m) => renderMessageRow(m))}
        <div ref={bottomRef} />
      </div>
      <div className="p-3 border-t border-glass flex gap-2 relative">
        <div className="flex-1 relative">
          {showMentions && mentionMatches.length > 0 && (
            <ul className="absolute bottom-full left-0 right-0 mb-1 max-h-40 overflow-y-auto glass-scroll glass-card shadow-lg z-10 text-sm">
              {mentionMatches.map((m) => (
                <li key={m.id}>
                  <button
                    type="button"
                    className="w-full text-left px-3 py-2 hover:bg-glass-2 text-glass-text"
                    onMouseDown={(e) => e.preventDefault()}
                    onClick={() => pickMention(m)}
                  >
                    <span className="font-medium">{m.fullName}</span>
                    <span className="text-xs text-glass-text-3 block">{m.email}</span>
                  </button>
                </li>
              ))}
            </ul>
          )}
          <Input
            ref={inputRef}
            value={body}
            onChange={(e) => {
              const v = e.target.value;
              setBody(v);
              syncMentionFromInput(v, e.target.selectionStart);
            }}
            onKeyDown={(e) => {
              if (showMentions && mentionMatches.length > 0 && e.key === 'Enter') {
                e.preventDefault();
                pickMention(mentionMatches[0]);
                return;
              }
              if (e.key === 'Escape' && showMentions) {
                e.preventDefault();
                setShowMentions(false);
                mentionAnchorRef.current = null;
                return;
              }
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                send();
              }
            }}
            onSelect={(e) => syncMentionFromInput(e.currentTarget.value, e.currentTarget.selectionStart)}
            placeholder="Type a message…"
            className="flex-1"
          />
        </div>
        <Button onClick={send} disabled={sending}>
          Send
        </Button>
      </div>
    </div>
  );
}
